/**
 * Redux Actions for Image Upload with Metadata
 *
 * PADRÃO: Modal-First Image Upload
 *
 * Fluxo Completo:
 * 1. USER: Seleciona arquivo
 * 2. MODAL: Abre e captura metadados (title, description, alt, caption)
 * 3. USER: Clica "Enviar Imagem"
 * 4. ACTION: uploadImageWithMetadata é disparada
 *    - Dispatch REQUEST (isUploading=true)
 *    - Lê arquivo via readAsDataURL
 *    - Cria objeto Image com metadados
 *    - Chama uploadContent do Volto
 * 5. ACTION: Aguarda resposta de uploadContent
 * 6. ACTION: Dispatch SUCCESS com dados da imagem criada (@id incluído)
 * 7. MODAL: Detecta SUCCESS e fecha
 * 8. WIDGET: Recebe dados via onSuccess e atualiza valor
 * 9. REDUX: closeImageModal zera state para permitir novo upload
 *
 * Estados Possíveis:
 * - IDLE: Inicial (isUploading=false, uploadedImage=null, error=null)
 * - REQUESTING: REQUEST disparado (isUploading=true)
 * - SUCCESS: Sucesso (isUploading=false, uploadedImage={...})
 * - FAILURE: Erro (isUploading=false, error="mensagem")
 */

import {
  CLOSE_IMAGE_MODAL,
  UPLOAD_IMAGE_WITH_METADATA_FAILURE,
  UPLOAD_IMAGE_WITH_METADATA_REQUEST,
  UPLOAD_IMAGE_WITH_METADATA_SUCCESS,
} from 'govbr.frontend.policy/constants/ActionTypes';
import { readAsDataURL } from 'promise-file-reader';

/**
 * Action Creator: uploadImageWithMetadata
 *
 * Faz upload de imagem com metadados via padrão Volto
 *
 * @param {string} path - Caminho do container (ex: '/pt-br/imagens')
 * @param {File} file - Arquivo a fazer upload
 * @param {Object} metadata - { title, description, alt, caption }
 * @param {Function} uploadContent - Action do Volto (injetada)
 * @param {string} blockId - ID do bloco relacionado (opcional)
 *
 * @returns {Function} Thunk function (dispatch, getState) => {}
 *
 * @example
 * dispatch(uploadImageWithMetadata(
 *   '/pt-br/imagens',
 *   fileObject,
 *   { title: 'Minha Imagem', alt: 'Descrição' },
 *   createContent,
 *   'block-123'
 * ))
 */
export const uploadImageWithMetadata =
  (path, file, metadata, uploadContent, blockId) => (dispatch, getState) => {
    // ============ VALIDAÇÕES ============
    if (!file) {
      dispatch({
        type: UPLOAD_IMAGE_WITH_METADATA_FAILURE,
        error: 'Arquivo não fornecido',
      });
      return;
    }

    if (!uploadContent) {
      dispatch({
        type: UPLOAD_IMAGE_WITH_METADATA_FAILURE,
        error: 'uploadContent não disponível',
      });
      return;
    }

    // ============ ESTADO: REQUEST ============
    dispatch({
      type: UPLOAD_IMAGE_WITH_METADATA_REQUEST,
    });

    // ============ LEITURA DO ARQUIVO ============
    try {
      readAsDataURL(file).then((dataUrl) => {
        // Parse dataURL para extrair mime type e dados
        const fields = dataUrl.match(/^data:(.*);(.*),(.*)$/);

        if (!fields || fields.length < 4) {
          throw new Error(
            'Não foi possível processar a imagem. Formato inválido.',
          );
        }

        const [, mimeType, encoding, base64Data] = fields;
        // ============ CONSTRUIR OBJETO IMAGE ============
        const imageContent = {
          '@type': 'Image',
          title: metadata.title || file.name,
          description: metadata.description || '',
          image_caption: metadata.caption || '',
          alt: metadata.alt || '',
          image: {
            data: base64Data,
            encoding: encoding,
            'content-type': mimeType,
            filename: file.name,
          },
        };

        // ============ CHAMAR uploadContent ============
        const uploadPromise = dispatch(
          uploadContent(path, imageContent, blockId || 'image-modal-upload'),
        );

        // ============ AGUARDAR RESPOSTA ============
        if (uploadPromise && typeof uploadPromise.then === 'function') {
          // uploadContent retornou uma Promise
          uploadPromise
            .then((uploadedData) => {
              // Disparar SUCCESS com dados da imagem criada (com @id!)
              dispatch({
                type: UPLOAD_IMAGE_WITH_METADATA_SUCCESS,
                payload: uploadedData || imageContent,
              });
            })
            .catch((uploadError) => {
              // Disparar FAILURE
              dispatch({
                type: UPLOAD_IMAGE_WITH_METADATA_FAILURE,
                error:
                  uploadError.message ||
                  'Erro desconhecido ao fazer upload da imagem',
              });
            });
        } else {
          dispatch({
            type: UPLOAD_IMAGE_WITH_METADATA_SUCCESS,
            payload: imageContent,
          });
        }
      });
    } catch (error) {
      dispatch({
        type: UPLOAD_IMAGE_WITH_METADATA_FAILURE,
        error: error.message || 'Erro desconhecido ao ler o arquivo',
      });
    }
  };

/**
 * Action Creator: closeImageModal
 *
 * Zera estado do modal (uploadedImage, error)
 * Permite novo upload ser feito
 *
 * @returns {Object} Redux action
 *
 * @example
 * dispatch(closeImageModal())
 */
export const closeImageModal = () => {
  return {
    type: CLOSE_IMAGE_MODAL,
  };
};
